import { Alert, Button, CircularProgress, Grid } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { usePostLogin } from '@/api/userAccount';
import { FormInputText } from '@/components/form/FormInputText';
import { useGlobalStore } from '@/store';

interface IFormInput {
    username: string;
    password: string;
}

export const LoginFormSection = () => {
    const { mutateAsync: postLogin, isError, error } = usePostLogin();
    const setIsLoggedIn = useGlobalStore((state) => state.setIsLoggedIn);
    const mixpanel = useMixpanel();
    const { handleSubmit, control, formState } = useForm<IFormInput>({
        defaultValues: {
            username: '',
            password: '',
        },
        mode: 'onChange',
    });

    const onLoginAttempt = async (data: IFormInput) => {
        try {
            const loginResult = await postLogin(data);
            if ('status' in loginResult && loginResult.status === 'success') {
                mixpanel?.identify(loginResult.username);
                mixpanel?.track('User Login', {
                    username: loginResult.username,
                });
                // No redirects here; the parent component (Login page) will handle navigation upon isLoggedIn state change
                setIsLoggedIn(true);
            } else if ('adminUsername' in loginResult) {
                // this is a wierdo admin login redirect result. let's call it logged in and see what happens.
                mixpanel?.identify(loginResult.adminUsername);
                mixpanel?.track('User Login', {
                    username: loginResult.adminUsername,
                    admin: true,
                });
                setIsLoggedIn(true);
            }
        } catch (error) {
            console.error('error while attempting to login', error);
        }
    };

    return (
        <>
            <Grid size={{ xs: 12 }}>
                <FormInputText
                    name="username"
                    control={control}
                    label="Email or username"
                    inputFieldProps={{
                        id: 'username',
                        type: 'text',
                        size: 'medium',
                        autoComplete: 'username',
                    }}
                    controllerProps={{
                        rules: {
                            required: 'Field is required',
                        },
                    }}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <FormInputText
                    name="password"
                    control={control}
                    label="Password"
                    inputFieldProps={{
                        id: 'password',
                        type: 'password',
                        size: 'medium',
                        autoComplete: 'current-password',
                    }}
                    controllerProps={{
                        rules: {
                            required: 'Field is required',
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ display: isError ? 'block' : 'none' }}>
                <Collapse in={isError} sx={{ width: '100%' }}>
                    <Alert severity="error">{`Error! ${error?.message}`}</Alert>
                </Collapse>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Button
                    type="submit"
                    onClick={handleSubmit(onLoginAttempt)}
                    fullWidth
                    variant="contained"
                    sx={{ mb: 2 }}
                    disabled={formState.isSubmitting}
                    endIcon={formState.isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    Sign in
                </Button>
            </Grid>
        </>
    );
};
